import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { DownloadTheApp, TrustpilotReviews } from 'wanderu-app-gettheapp';
import {
    CarrierSection,
    InfoSection,
    TableOfContentsSection,
} from 'wanderu-app-seo';
import { Footer } from 'wanderu-component-footer';
import Helmet from 'wanderu-component-helmet';
import BaseHeroHeader from 'wanderu-component-hero-header';
import { NotFound } from 'wanderu-component-not-found';
import {
    BusLoader,
    FAQSection,
    Section,
    ValueProps,
    ValueStatement,
} from 'wanderu-components';
import {
    i18nCoordinator,
    localize,
    selectLocale,
} from 'wanderu-i18n';
import { Widget } from 'wanderu-widget';

import AirportOverview from '../../components/AirportOverview';
import HotelsSection from '../../components/HotelsSection/HotelsSection';
import InTheArea from '../../components/InTheArea/InTheArea';
import RoutesTable from '../../components/RoutesTable/RoutesTable';
import ShuttleSection from '../../components/ShuttlesSection';
import { NAV_ITEMS } from '../../constants';
import {
    fetchShuttleGuideAction,
    shuttleGuideSelectors,
} from '../../ducks';
import shuttleGuideConfig from '../../locales.config';
import {
    isInvalidUrl,
    localizeStructuredData,
} from '../../utils';

const { namespace, supportedLocales } = shuttleGuideConfig;

const propTypes = {
    airport: PropTypes.string,
    airportImages: PropTypes.arrayOf(
        PropTypes.shape({
            metadata: PropTypes.shape({
                alt: PropTypes.string,
                author: PropTypes.string,
            }),
            url: PropTypes.string,
        })
    ),
    carriers: PropTypes.arrayOf(PropTypes.object),
    content: PropTypes.shape(),
    faqContent: PropTypes.arrayOf(
        PropTypes.shape({
            prefix: PropTypes.string,
            text: PropTypes.string,
            title: PropTypes.string,
        })
    ),
    fetchContent: PropTypes.func.isRequired,
    hotelsContent: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            title: PropTypes.string,
        })
    ),
    isFetching: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    location: PropTypes.shape(),
    match: PropTypes.shape().isRequired,
    metaImage: PropTypes.shape({
        alt: PropTypes.string,
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
        src: PropTypes.string.isRequired,
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
    }),
    nearbyContent: PropTypes.arrayOf(
        PropTypes.shape({
            stat: PropTypes.shape({
                mi: PropTypes.number,
            }),
            text: PropTypes.string,
        })
    ),
    notFound: PropTypes.bool,
    routeItems: PropTypes.arrayOf(
        PropTypes.shape({
            avgPrice: PropTypes.number,
            avgTime: PropTypes.number,
            dailyTrips: PropTypes.number,
            destination: PropTypes.string,
            url: PropTypes.string,
        })
    ),
    shuttles: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            logo: PropTypes.string,
            name: PropTypes.string,
            phoneNumber: PropTypes.string,
            website: PropTypes.string,
        })
    ),
    staticContext: PropTypes.shape({
        status: PropTypes.number,
    }),
    structuredData: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            mainEntity: PropTypes.arrayOf(
                PropTypes.shape({
                    acceptedAnswer: PropTypes.shape({
                        text: PropTypes.string,
                    }),
                    name: PropTypes.string,
                })
            ),
        })
    ),
    t: PropTypes.func,
    url: PropTypes.string,
};

const defaultProps = {
    content: {},
    location: {},
};

function AirportShuttlesGuide({
    airport,
    airportImages,
    carriers,
    content,
    faqContent,
    fetchContent,
    hotelsContent,
    isFetching,
    locale,
    location,
    metaImage,
    nearbyContent,
    notFound,
    routeItems,
    shuttles,
    staticContext,
    structuredData,
    t,
    url,
}) {
    useEffect(() => {
        i18nCoordinator.registerTranslatableApp({
            ...shuttleGuideConfig,
            translationFetchFn: () => { },
        });

        return () => {
            i18nCoordinator.unregisterTranslatableApp(namespace);
        };
    }, []);

    useEffect(() => {
        if (Object.keys(content).length) {
            return;
        }

        fetchContent(airport, locale);
    }, [content, fetchContent, locale, airport]);

    useEffect(() => {
        if (!Object.keys(content).length) {
            return;
        }

        i18nCoordinator.updateTranslations(locale, content, namespace);
    }, [content, locale]);

    if (notFound) {
        if (staticContext) {
            staticContext.status = 404;
        }
        return <NotFound status={staticContext?.status} />;
    }

    if (isFetching || !Object.keys(content).length) {
        return (
            <BusLoader large />
        );
    }

    const localizedStructuredData = localizeStructuredData({
        locale,
        structuredData,
        t,
    });

    const filterNavItems = navItems => navItems.filter(item => {
        if (item.anchor === '#map-header-section' && !t('mapDescription')) {
            return false;
        }

        if (item.anchor === '#carrier-section' && !carriers?.length) {
            return false;
        }

        if (item.anchor === '#routes-header-section' && !routeItems?.length) {
            return false;
        }

        return true;
    });

    return (
        <>
            <Helmet
                canonicalPath={t('canonicalSlug')}
                description={t('metaPageDescription')}
                image={metaImage}
                structuredData={localizedStructuredData}
                supportedLocales={supportedLocales}
                title={t('metaPageTitle')}
            />
            <BaseHeroHeader
                title={t('widgetTitle')}
                widgetProps={{
                    defaultOriginInput: location.name,
                    defaultOriginPlaceId: location.placeId,
                }}
            />
            <Section>
                <ValueProps />
            </Section>
            <TrustpilotReviews locale={locale} />
            <DownloadTheApp campaign="web-airport_shuttles_guide" />
            <TableOfContentsSection
                namespace={namespace}
                navItems={filterNavItems(NAV_ITEMS)}
            />
            {!!carriers?.length && (
                <CarrierSection
                    data={carriers}
                    description={t('carriersDescription')}
                    namespace={namespace}
                    title={t('carriersHeader')}
                    hideCarrierStatsTable
                />
            )}
            {!!routeItems?.length && (
                <Section id="routes-header-section">
                    <RoutesTable
                        namespace={namespace}
                        routeItems={routeItems}
                    />
                </Section>
            )}
            <AirportOverview
                airportImages={airportImages}
                description={t('introDescription')}
                id="about-section"
                namespace={namespace}
                title={t('introHeader', {
                    airportName: t('airportName'),
                })}
            />
            <HotelsSection
                hotelsContent={hotelsContent}
                namespace={namespace}
                sectionDescription={t('hotelsSectionDescription')}
                sectionTitle={t('hotelsSectionHeader')}
            />
            <Section aria-labelledby="nearby-header" id="nearby-header-section">
                <InTheArea
                    places={nearbyContent}
                    title={t('nearbyHeader')}
                />
            </Section>
            <FAQSection title={t('faqHeader')}>
                {faqContent.map((info) => (
                    <InfoSection
                        id={info.prefix}
                        infoText={info.text}
                        infoTitle={t(info.title)}
                        isOpen={false}
                        key={info.prefix}
                    />
                ))}
            </FAQSection>
            <ShuttleSection
                description={t('shuttlesDescription')}
                shuttles={shuttles}
                title={t('shuttlesHeader')}
                url={url}
            />
            <ValueStatement namespace={'baseApp'} />
            <Footer limitLinkFlow />
        </>
    );
}

AirportShuttlesGuide.serverFetchData = async (opts) => {
    const {
        dispatch,
        params,
        getState,
        i18nCoordinator: serverI18nCoordinator,
    } = opts;

    const { airportSlug, locale } = params;

    if (isInvalidUrl(airportSlug)) {
        return;
    }

    const airport = airportSlug.split('--')[1].toUpperCase();
    const airportId = `en-us-${airport.replace('/', '')}`;

    await dispatch(fetchShuttleGuideAction.fetchPageContent(airport, locale));

    await Promise.all([
        serverI18nCoordinator.registerTranslatableApp({
            ...shuttleGuideConfig,
            translationFetchFn: () => {
                const apiTranslations = shuttleGuideSelectors.selectContent(getState(), airportId);

                return {
                    ...apiTranslations,
                };
            },
        }),
        Widget.serverFetchData(opts),
    ]);
};

const mapStateToProps = (state, ownProps) => {
    const { url } = ownProps.match;

    if (isInvalidUrl(url)) {
        return {
            notFound: true,
        };
    }

    const airport = url.split('--')[1].replace('/', '').toUpperCase();
    const locale = selectLocale(state);
    const airportId = `${locale}-${airport}`;
    const content = shuttleGuideSelectors.selectContent(state, airportId);
    const faqContent = content
        ? shuttleGuideSelectors.selectFaqContent(state, airportId)
        : [];
    const noContent = !content || !Object.keys(content).length;
    const fetching = !fetchShuttleGuideAction.selectError(state, airportId)
        && !fetchShuttleGuideAction.selectSuccess(state, airportId);
    const notFound = noContent && !fetching;
    const airportImages = shuttleGuideSelectors.selectAirportImages(state, airportId) || [];
    const metaImage = shuttleGuideSelectors.selectMetaImage(state, airportId);
    const location = shuttleGuideSelectors.selectLocation(state, airportId);

    return {
        airport,
        airportImages,
        carriers: shuttleGuideSelectors.selectCarriers(state, airportId),
        content,
        faqContent,
        hotelsContent: shuttleGuideSelectors.selectHotels(state, airportId),
        isFetching: fetchShuttleGuideAction.selectWorking(state, airportId),
        locale,
        location,
        metaImage,
        nearbyContent: shuttleGuideSelectors.selectNearbyContent(state, airportId),
        notFound,
        routeItems: shuttleGuideSelectors.selectRoutes(state, airportId),
        shuttles: shuttleGuideSelectors.selectShuttles(state, airportId),
        structuredData: shuttleGuideSelectors.selectStructuredData(
            state,
            airportId,
        ),
        url,
    };
};

const mapDispatchToProps = {
    fetchContent: fetchShuttleGuideAction.fetchPageContent,
};

AirportShuttlesGuide.propTypes = propTypes;
AirportShuttlesGuide.defaultProps = defaultProps;

const localizedAirportShuttlesGuide = localize(AirportShuttlesGuide, ['t'], shuttleGuideConfig.namespace);
// eslint-disable-next-line react-redux/prefer-separate-component-file
export default connect(mapStateToProps, mapDispatchToProps)(localizedAirportShuttlesGuide);
